import {
  StoreScheduleExclusionResponse,
  StoreScheduleStoreExclusionResponse,
} from '@ydistri/api-sdk';
import { apiSlice, ErrorType, TemplateOrCalculation } from '../../../apis/api';
import { getTags } from '../../../apis/apiLib';
import { signalrClient } from '../../../signalr/client';
import { CalculationsCollection, TemplatesCollection } from '../../../swagger/collections';
import { SignalProjectConfigurationChanged } from '../../ProjectAdministration/common/administrationItemsTypes';

const { TAGS, TAGS_ARRAY } = getTags('storeScheduleExclusions');

interface CacheEntryLifecycle {
  cacheDataLoaded: Promise<unknown>;
  cacheEntryRemoved: Promise<unknown>;
  dispatch: (action: ReturnType<typeof apiSlice.util.invalidateTags>) => unknown;
}

/**
 * A store schedule is project-wide, so a change made anywhere - another screen, another user - can
 * add or remove what this template excludes. The payloads are derived per template, so the signal
 * only triggers a re-read rather than patching the cache.
 */
const rereadOnStoreScheduleChange = async (api: CacheEntryLifecycle) => {
  try {
    await api.cacheDataLoaded;

    const listener = (signal: SignalProjectConfigurationChanged) => {
      if (signal.entityType !== 'StoreSchedule') return;
      api.dispatch(apiSlice.util.invalidateTags([TAGS.storeScheduleExclusions]));
    };

    signalrClient.on('ProjectConfigurationChanged', listener);

    api.cacheEntryRemoved.then(() => {
      signalrClient.off('ProjectConfigurationChanged', listener);
    });
  } catch {
    // no-op in case cache entry was removed before data was loaded
  }
  await api.cacheEntryRemoved;
};

export const apiStoreScheduleExclusions = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getStoreScheduleExclusions: builder.query<
        StoreScheduleExclusionResponse[],
        TemplateOrCalculation
      >({
        queryFn: async arg => {
          try {
            const response = await (arg.type === 'Template'
              ? TemplatesCollection.getTemplateStoreScheduleExclusions(arg.id)
              : CalculationsCollection.getCalculationStoreScheduleExclusions(arg.id));
            return { data: response.data.data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        providesTags: [TAGS.storeScheduleExclusions],
        onCacheEntryAdded: (arg, api) => rereadOnStoreScheduleChange(api),
      }),
      /**
       * What each store ends up excluded from, aggregated by the backend: a store with a send-only
       * and a receive-only record is completely excluded, which neither record says alone.
       */
      getStoreScheduleExclusionsByStore: builder.query<
        StoreScheduleStoreExclusionResponse[],
        TemplateOrCalculation
      >({
        queryFn: async arg => {
          try {
            const response = await (arg.type === 'Template'
              ? TemplatesCollection.getTemplateStoreScheduleExclusionsByStore(arg.id)
              : CalculationsCollection.getCalculationStoreScheduleExclusionsByStore(arg.id));
            return { data: response.data.data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        providesTags: [TAGS.storeScheduleExclusions],
        onCacheEntryAdded: (arg, api) => rereadOnStoreScheduleChange(api),
      }),
      setStoreScheduleExclusion: builder.mutation<
        void,
        { templateId: number; storeScheduleId: number; isEnabled: boolean }
      >({
        queryFn: async ({ templateId, storeScheduleId, isEnabled }) => {
          try {
            await TemplatesCollection.putTemplateStoreScheduleExclusion(
              templateId,
              storeScheduleId,
              { isEnabled },
            );
            return { data: undefined };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        // The box shows the new value from the click, not from the round trip. Without this it
        // stays on the stored value until the re-read lands, so it would tick back on and then off
        // again once the request settled.
        async onQueryStarted({ templateId, storeScheduleId, isEnabled }, api) {
          const patch = api.dispatch(
            apiStoreScheduleExclusions.util.updateQueryData(
              'getStoreScheduleExclusions',
              { type: 'Template', id: templateId },
              draft => {
                const row = draft.find(e => e.storeScheduleId === storeScheduleId);
                if (row) row.isEnabled = isEnabled;
              },
            ),
          );

          await api.queryFulfilled.catch(patch.undo);
        },
        invalidatesTags: [TAGS.storeScheduleExclusions],
      }),
    }),
  });

export const {
  useGetStoreScheduleExclusionsQuery,
  useGetStoreScheduleExclusionsByStoreQuery,
  useSetStoreScheduleExclusionMutation,
} = apiStoreScheduleExclusions;
